import axios from 'axios';
import authService from './authService';
import { API_BASE_URL } from '../config/api';

const ORDERS_URL = `${API_BASE_URL}/purchases/purchase-orders/`;
const PAYMENT_METHODS_URL = `${API_BASE_URL}/purchases/payment-methods/`;
const PURCHASE_TYPES_URL = `${API_BASE_URL}/purchases/purchase-types/`;

const getHeaders = () => ({
  'Authorization': `Token ${authService.getToken()}`,
  'Content-Type': 'application/json'
});

/**
 * Catalog endpoints are paginated like everything else, but their consumers
 * are dropdowns that only ever want the rows. Unwrapping here keeps that
 * detail in one place instead of in every caller.
 */
const unwrapRows = (response) => ({
  ...response,
  data: response.data?.results ?? []
});

const purchasesService = {
  /**
   * Lists purchase orders. Takes an options object rather than positional
   * arguments because the backend exposes seven independent filters; empty
   * filters are omitted so the API never receives a blank value to match on.
   */
  listPurchaseOrders: ({
    search = null,
    effectiveDateStart = null,
    effectiveDateEnd = null,
    categoryId = null,
    subcategoryId = null,
    ordering = '-effective_date',
    page = 1,
    includeInactive = false
  } = {}) => {
    const params = { ordering, page };

    if (search) params.search = search;
    if (effectiveDateStart) params.effective_date_start = effectiveDateStart;
    if (effectiveDateEnd) params.effective_date_end = effectiveDateEnd;
    if (categoryId) params.category_id = categoryId;
    if (subcategoryId) params.subcategory_id = subcategoryId;
    if (includeInactive) params.include_inactive = 'true';

    return axios.get(ORDERS_URL, { headers: getHeaders(), params });
  },

  getPurchaseOrderById: (id) => {
    return axios.get(`${ORDERS_URL}${id}/`, { headers: getHeaders() });
  },

  createPurchaseOrder: (data) => {
    return axios.post(ORDERS_URL, data, { headers: getHeaders() });
  },

  updatePurchaseOrder: (id, data) => {
    return axios.patch(`${ORDERS_URL}${id}/`, data, { headers: getHeaders() });
  },

  /** DELETE is a soft cancel server-side (sets is_active=False). */
  cancelPurchaseOrder: (id) => {
    return axios.delete(`${ORDERS_URL}${id}/`, { headers: getHeaders() });
  },

  /** Flips is_active, so a cancelled order can be brought back. */
  toggleActive: (id) => {
    return axios.patch(`${ORDERS_URL}${id}/toggle_active/`, {}, { headers: getHeaders() });
  },

  /**
   * Registers the payment: the account the money left and the date. is_paid
   * is read-only on the serializer, so this action is the only way to set it
   * — a payment has to leave an accounting entry behind.
   */
  markPaid: (id, payload) => {
    return axios.post(`${ORDERS_URL}${id}/mark_paid/`, payload, { headers: getHeaders() });
  },

  /** Undoes the payment and deactivates the movement it created. */
  markUnpaid: (id) => {
    return axios.post(`${ORDERS_URL}${id}/mark_unpaid/`, {}, { headers: getHeaders() });
  },

  /**
   * Catalog feed for the payment-method dropdown. The endpoint uses the
   * project default pagination, so the raw body is {count, next, previous,
   * results}; the rows are unwrapped here so callers always receive an array.
   */
  listPaymentMethods: async () => {
    const response = await axios.get(PAYMENT_METHODS_URL, { headers: getHeaders() });
    return unwrapRows(response);
  },

  /**
   * Catalog feed for the classification dropdowns; parentId drives the
   * category -> subcategory cascade. Paginated like every other list
   * endpoint, unwrapped to an array for callers.
   */
  listPurchaseTypes: async ({ type = null, parentId = null } = {}) => {
    const params = {};
    if (type) params.type = type;
    if (parentId) params.parent_purchase_type = parentId;

    const response = await axios.get(PURCHASE_TYPES_URL, {
      headers: getHeaders(),
      params
    });
    return unwrapRows(response);
  }
};

export default purchasesService;
